// Oude matras retour cart sync — keeps the retour line item locked to its parent product:
//   - quantity matches the parent (1 boxspring -> 1 retour; 2 -> 2; etc.)
//   - removed automatically once the parent is gone
// Coupling lives in two line item properties added at PDP add-to-cart time (assets/oude-matras-retour.js)
// or from the cart toggle (assets/retour-cart-toggle.js):
//   _retour_pair_id   shared between parent and addon
//   _is_retour_addon  marker on the addon line only
// Underscore-prefixed properties are hidden by the cart renderer.
// Mirror of assets/montage-cart-sync.js.
import { CartUpdateEvent, ThemeEvents } from "@theme/events";

const RETOUR_PAIR_KEY = "_retour_pair_id";
const RETOUR_ADDON_KEY = "_is_retour_addon";

function getRoutes() {
  const r = (window.MorgennTheme && window.MorgennTheme.routes) || {};
  return {
    cart: r.cart || "/cart.js",
    cartChange: r.cart_change_url || "/cart/change.js",
  };
}

function collectSectionIds() {
  const ids = new Set();
  document.querySelectorAll("cart-items-component[data-section-id]").forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  document.querySelectorAll("cart-drawer-component[data-section-id]").forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

function readRetourProps(item) {
  let isAddon = false;
  let pairId = null;
  const props = item && item.properties;
  if (props && typeof props === "object") {
    for (const k of Object.keys(props)) {
      if (k === RETOUR_ADDON_KEY) isAddon = true;
      if (k === RETOUR_PAIR_KEY) pairId = props[k];
    }
  }
  return { isAddon, pairId };
}

class RetourCartSync {
  constructor() {
    this._syncing = false;
    this._pending = false;
    this._onCartUpdate = this.onCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this._onCartUpdate);
  }

  async onCartUpdate(event) {
    const source = event && event.detail && event.detail.data && event.detail.data.source;
    if (source === "retour-cart-sync") return;

    if (this._syncing) {
      this._pending = true;
      return;
    }

    this._syncing = true;
    try {
      await this.syncRetourLines();
      while (this._pending) {
        this._pending = false;
        await this.syncRetourLines();
      }
    } catch (err) {
      console.error("[retour-cart-sync]", err);
    } finally {
      this._syncing = false;
    }
  }

  async syncRetourLines() {
    const routes = getRoutes();
    const cartRes = await fetch(routes.cart, { headers: { Accept: "application/json" } });
    if (!cartRes.ok) return;
    const cart = await cartRes.json();
    const items = (cart && cart.items) || [];

    const parentByPair = new Map();
    for (const item of items) {
      const { isAddon, pairId } = readRetourProps(item);
      if (!isAddon && pairId) parentByPair.set(pairId, item);
    }

    const sectionIds = collectSectionIds();
    const sectionsParam = sectionIds.length > 0 ? sectionIds.join(",") : null;
    let lastResponse = null;
    let changed = false;

    for (const item of items) {
      const { isAddon, pairId } = readRetourProps(item);
      if (!isAddon || !pairId) continue;

      const parent = parentByPair.get(pairId);
      let desiredQty;
      if (!parent) {
        desiredQty = 0;
      } else if (parent.quantity !== item.quantity) {
        desiredQty = parent.quantity;
      } else {
        continue;
      }

      const body = { id: item.key, quantity: desiredQty };
      if (sectionsParam) body.sections = sectionsParam;

      try {
        const res = await fetch(routes.cartChange, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) continue;
        lastResponse = await res.json();
        changed = true;
      } catch (err) {
        console.error("[retour-cart-sync] change failed", err);
      }
    }

    if (changed) {
      document.dispatchEvent(
        new CartUpdateEvent(lastResponse || {}, "retour-cart-sync", {
          itemCount: lastResponse && lastResponse.item_count,
          source: "retour-cart-sync",
          sections: lastResponse && lastResponse.sections,
        })
      );
    }
  }
}

if (!window.__retourCartSyncInstance) {
  window.__retourCartSyncInstance = new RetourCartSync();
}
